import {type ReactNode, useCallback, useEffect, useMemo, useState} from 'react'
import {
  AlertCircle,
  Archive,
  Boxes,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleSlash2,
  Filter,
  Loader2,
  PackageCheck,
  Pencil,
  RefreshCw,
  Search,
  X,
} from 'lucide-react'
import {ProductThumbnail} from '../shared/components/ProductThumbnail'
import {CardHelp} from '../shared/components/CardHelp'
import {
  getSellerProducts,
  ProductLifecycleStatus,
  SellerManagedProduct,
  updateSellerProductStatus,
} from '../services/productsApi'
import {SellerPageLoader} from '../shared/components/SellerPageLoader'

const PAGE_SIZE = 50

const statusConfig: Record<ProductLifecycleStatus, {label: string; classes: string; description: string}> = {
  active: {
    label: 'Ativo',
    classes: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    description: 'O produto participa normalmente dos c\u00e1lculos e pedidos.',
  },
  discontinued: {
    label: 'Fora de linha',
    classes: 'border-red-200 bg-red-50 text-red-700',
    description: 'Produto descontinuado pela ind\u00fastria ou definitivamente fora do mix.',
  },
  not_used: {
    label: 'N\u00e3o trabalha mais',
    classes: 'border-amber-200 bg-amber-50 text-amber-700',
    description: 'O lojista n\u00e3o utiliza mais este produto, mesmo que ainda existam registros.',
  },
}

function formatDate(value?: string | null) {
  if (!value) return '\u2014'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '\u2014' : date.toLocaleDateString('pt-BR')
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 0}).format(value || 0)
}

function formatCnpj(value: string) {
  const digits = value.replace(/\D/g, '')
  if (digits.length !== 14) return value
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`
}

export function ProductsPage() {
  const [products, setProducts] = useState<SellerManagedProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | ProductLifecycleStatus>('all')
  const [industryFilter, setIndustryFilter] = useState('all')
  const [cnpjFilter, setCnpjFilter] = useState('all')
  const [supplierFilter, setSupplierFilter] = useState('all')
  const [stockFilter, setStockFilter] = useState<'all' | 'with_stock' | 'without_stock'>('all')
  const [page, setPage] = useState(1)
  const [selectedProduct, setSelectedProduct] = useState<SellerManagedProduct | null>(null)
  const [draftStatus, setDraftStatus] = useState<ProductLifecycleStatus>('active')
  const [statusNote, setStatusNote] = useState('')

  const [statusScope, setStatusScope] = useState('*')
  const loadProducts = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await getSellerProducts()
      setProducts(response.products)
    } catch (requestError: any) {
      setError(
        requestError?.response?.data?.error ||
          requestError?.message ||
          'N\u00e3o foi poss\u00edvel carregar os produtos.'
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadProducts()
  }, [loadProducts])

  const industries = useMemo(
    () => Array.from(new Set(products.map((product) => product.industry).filter(Boolean))).sort(),
    [products]
  )
  const cnpjs = useMemo(
    () => Array.from(new Set(products.flatMap((product) => product.cnpjs.map((item) => item.cnpj)))).sort(),
    [products]
  )
  const suppliers = useMemo(() => {
    const rows = new Map<number, string>()
    products.forEach((product) =>
      product.suppliers.forEach((supplier) => rows.set(supplier.supplier_id, supplier.fornecedor))
    )
    return Array.from(rows, ([supplier_id, fornecedor]) => ({supplier_id, fornecedor})).sort((a, b) =>
      a.fornecedor.localeCompare(b.fornecedor, 'pt-BR')
    )
  }, [products])

  const displaySummary = useMemo(() => {
    const scoped = cnpjFilter === 'all'
      ? products
      : products.filter((product) => product.cnpjs.some((item) => item.cnpj === cnpjFilter))
    const statuses = scoped.map((product) =>
      cnpjFilter === 'all'
        ? product.lifecycle_status
        : product.cnpjs.find((item) => item.cnpj === cnpjFilter)?.lifecycle_status || product.lifecycle_status
    )
    return {
      total: scoped.length,
      active: statuses.filter((status) => status === 'active').length,
      discontinued: statuses.filter((status) => status === 'discontinued').length,
      not_used: statuses.filter((status) => status === 'not_used').length,
    }
  }, [cnpjFilter, products])

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR')
    return products.filter((product) => {
      const cnpjData = cnpjFilter === 'all'
        ? undefined
        : product.cnpjs.find((item) => item.cnpj === cnpjFilter)
      if (cnpjFilter !== 'all' && !cnpjData) return false
      if (
        term &&
        !`${product.product_name} ${product.ean} ${product.description}`
          .toLocaleLowerCase('pt-BR')
          .includes(term)
      ) return false
      if (statusFilter !== 'all' && (cnpjData?.lifecycle_status || product.lifecycle_status) !== statusFilter) return false
      if (industryFilter !== 'all' && product.industry !== industryFilter) return false
      if (supplierFilter === 'registered' && product.suppliers.length === 0) return false
      if (
        supplierFilter !== 'all' &&
        supplierFilter !== 'registered' &&
        !product.suppliers.some((supplier) => String(supplier.supplier_id) === supplierFilter)
      ) return false
      const currentStock = cnpjData?.current_stock ?? product.current_stock
      if (stockFilter === 'with_stock' && currentStock <= 0) return false
      if (stockFilter === 'without_stock' && currentStock > 0) return false
      return true
    })
  }, [cnpjFilter, industryFilter, products, search, statusFilter, stockFilter, supplierFilter])

  useEffect(() => {
    setPage(1)
  }, [cnpjFilter, industryFilter, search, statusFilter, stockFilter, supplierFilter])

  const pageCount = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE))
  const currentPage = Math.min(page, pageCount)
  const visibleProducts = filteredProducts.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  )

  const openStatusDialog = (product: SellerManagedProduct) => {
    const initialScope = cnpjFilter !== 'all' && product.cnpjs.some((item) => item.cnpj === cnpjFilter)
      ? cnpjFilter
      : '*'
    const initialState = initialScope === '*'
      ? product
      : product.cnpjs.find((item) => item.cnpj === initialScope) || product
    setSelectedProduct(product)
    setStatusScope(initialScope)
    setDraftStatus(initialState.lifecycle_status)
    setStatusNote(initialState.status_note || '')
    setError('')
    setSuccess('')
  }

  const changeStatusScope = (scope: string) => {
    if (!selectedProduct) return
    const state = scope === '*' ? selectedProduct : selectedProduct.cnpjs.find((item) => item.cnpj === scope) || selectedProduct
    setStatusScope(scope)
    setDraftStatus(state.lifecycle_status)
    setStatusNote(state.status_note || '')
  }

  const saveStatus = async () => {
    if (!selectedProduct) return
    setSaving(true)
    setError('')
    try {
      const updated = await updateSellerProductStatus(
        selectedProduct.ean,
        draftStatus,
        statusNote.trim(),
        statusScope
      )
      setProducts((current) => {
        const next = current.map((product) => (product.ean === updated.ean ? updated : product))
        return next
      })
      setSelectedProduct(null)
      setSuccess(
        draftStatus === 'active'
          ? `${updated.product_name} voltou a participar dos c\u00e1lculos.`
          : `${updated.product_name} ser\u00e1 omitido dos c\u00e1lculos e novos pedidos.`
      )
    } catch (requestError: any) {
      setError(
        requestError?.response?.data?.error ||
          requestError?.message ||
          'N\u00e3o foi poss\u00edvel atualizar o produto.'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='space-y-6'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between'>
        <div>
          <div className='mb-2 flex items-center gap-2 text-sm font-semibold text-blue-600'>
            <Boxes className='h-4 w-4' />
            Cat&aacute;logo trabalhado pelo lojista
          </div>
          <h1 className='text-3xl font-bold text-gray-900'>Produtos</h1>
          <p className='mt-2 max-w-3xl text-sm leading-6 text-gray-500'>
            Gerencie todos os EANs j&aacute; importados nesta conta. A situa&ccedil;&atilde;o pode valer para todos os CNPJs
            ou somente para um estabelecimento espec&iacute;fico.
          </p>
        </div>
        <button
          type='button'
          onClick={loadProducts}
          disabled={loading}
          className='inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50'
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      <div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
        <SummaryCard icon={Boxes} label='Produtos registrados' value={displaySummary.total} color='blue' help='Produtos com algum registro importado para o lojista e para o filtro de CNPJ selecionado.' />
        <SummaryCard icon={PackageCheck} label={'Ativos nos c\u00e1lculos'} value={displaySummary.active} color='emerald' help='Produtos que continuam participando dos cálculos e sugestões de reposição.' />
        <SummaryCard icon={Archive} label='Fora de linha' value={displaySummary.discontinued} color='red' help='Produtos marcados como descontinuados e omitidos dos cálculos operacionais.' />
        <SummaryCard icon={CircleSlash2} label={'N\u00e3o trabalha mais'} value={displaySummary.not_used} color='amber' help='Produtos que o lojista informou não comercializar mais.' />
      </div>

      {(error || success) && (
        <div
          className={`flex items-center gap-3 rounded-xl border p-4 text-sm ${
            error
              ? 'border-red-200 bg-red-50 text-red-800'
              : 'border-emerald-200 bg-emerald-50 text-emerald-800'
          }`}
        >
          {error ? <AlertCircle className='h-5 w-5 flex-none' /> : <CheckCircle2 className='h-5 w-5 flex-none' />}
          <span>{error || success}</span>
        </div>
      )}

      <section className='rounded-2xl border border-gray-200 bg-white shadow-sm'>
        <div className='border-b border-gray-200 p-5'>
          <div className='mb-4 flex items-center gap-2 text-sm font-semibold text-gray-700'>
            <Filter className='h-4 w-4 text-blue-600' />
            Filtros
          </div>
          <div className='grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-7'>
            <label className='relative 2xl:col-span-2'>
              <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={'Buscar por produto, descri\u00e7\u00e3o ou EAN'}
                className='w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-4 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
              />
            </label>
            <FilterSelect value={statusFilter} onChange={(value) => setStatusFilter(value as typeof statusFilter)}>
              <option value='all'>Todos os status</option>
              <option value='active'>Ativos</option>
              <option value='discontinued'>Fora de linha</option>
              <option value='not_used'>N&atilde;o trabalha mais</option>
            </FilterSelect>
            <FilterSelect value={cnpjFilter} onChange={setCnpjFilter}>
              <option value='all'>Todos os CNPJs</option>
              {cnpjs.map((cnpj) => <option key={cnpj} value={cnpj}>{formatCnpj(cnpj)}</option>)}
            </FilterSelect>
            <FilterSelect value={industryFilter} onChange={setIndustryFilter}>
              <option value='all'>Todas as ind&uacute;strias</option>
              {industries.map((industry) => <option key={industry} value={industry}>{industry}</option>)}
            </FilterSelect>
            <FilterSelect value={supplierFilter} onChange={setSupplierFilter}>
              <option value='all'>Todos os fornecedores</option>
              <option value='registered'>Somente fornecedores cadastrados</option>
              {suppliers.map((supplier) => (
                <option key={supplier.supplier_id} value={supplier.supplier_id}>{supplier.fornecedor}</option>
              ))}
            </FilterSelect>
            <FilterSelect value={stockFilter} onChange={(value) => setStockFilter(value as typeof stockFilter)}>
              <option value='all'>Com ou sem estoque</option>
              <option value='with_stock'>Somente com estoque</option>
              <option value='without_stock'>Somente sem estoque</option>
            </FilterSelect>
          </div>
        </div>

        {loading ? (
          <SellerPageLoader
            compact
            title='Carregando produtos'
            description={'Organizando cat\u00e1logo, fornecedores e situa\u00e7\u00f5es cadastradas.'}
          />
        ) : visibleProducts.length === 0 ? (
          <div className='py-24 text-center'>
            <Boxes className='mx-auto h-11 w-11 text-gray-300' />
            <h2 className='mt-4 font-semibold text-gray-800'>Nenhum produto encontrado</h2>
            <p className='mt-1 text-sm text-gray-500'>Ajuste os filtros ou realize uma importa&ccedil;&atilde;o de dados.</p>
          </div>
        ) : (
          <>
            <div className='overflow-x-auto'>
              <table className='min-w-[1080px] w-full divide-y divide-gray-200'>
                <thead className='bg-gray-50'>
                  <tr className='text-left text-xs font-semibold uppercase tracking-wide text-gray-500'>
                    <th className='px-5 py-3'>Produto</th>
                    <th className='px-5 py-3'>Ind&uacute;stria</th>
                    <th className='px-5 py-3'>Fornecedores</th>
                    <th className='px-5 py-3 text-right'>CNPJs</th>
                    <th className='px-5 py-3 text-right'>Estoque</th>
                    <th className='px-5 py-3 text-right'>Sa&iacute;das</th>
                    <th className='px-5 py-3'>&Uacute;ltimo import</th>
                    <th className='px-5 py-3'>Situa&ccedil;&atilde;o</th>
                    <th className='px-5 py-3 text-right'>A&ccedil;&atilde;o</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {visibleProducts.map((product) => {
                    const cnpjData = cnpjFilter === 'all'
                      ? undefined
                      : product.cnpjs.find((item) => item.cnpj === cnpjFilter)
                    const lifecycleStatus = cnpjData?.lifecycle_status || product.lifecycle_status
                    const status = statusConfig[lifecycleStatus]
                    const statusNote = cnpjData?.status_note || product.status_note
                    const mixedStatus = cnpjFilter === 'all' && product.is_mixed_status
                    return (
                      <tr key={product.ean} className={lifecycleStatus === 'active' ? 'hover:bg-gray-50/70' : 'bg-gray-50/50 text-gray-500'}>
                        <td className='px-5 py-4'>
                          <div className='flex items-center gap-3'>
                            <ProductThumbnail src={product.foto} alt={product.product_name} ean={product.ean} details={{
                              status: lifecycleStatus, statusLabel: mixedStatus ? 'Situação mista' : status.label,
                              estoqueAtual: cnpjData?.current_stock ?? product.current_stock,
                              fornecedor: product.suppliers.map((supplier) => supplier.fornecedor).join(', ') || undefined,
                            }} />
                            <div className='min-w-0'>
                              <p className='max-w-[300px] truncate font-semibold text-gray-900'>{product.product_name}</p>
                              <p className='mt-1 font-mono text-xs text-gray-500'>EAN: {product.ean}</p>
                              {statusNote && <p className='mt-1 max-w-[300px] truncate text-xs text-amber-700'>{statusNote}</p>}
                            </div>
                          </div>
                        </td>
                        <td className='px-5 py-4 text-sm text-gray-600'>{product.industry || '\u2014'}</td>
                        <td className='px-5 py-4 text-sm text-gray-600'>
                          <p className='max-w-[220px] truncate'>
                            {product.suppliers.map((supplier) => supplier.fornecedor).join(', ') || 'Sem fornecedor'}
                          </p>
                        </td>
                        <td className='px-5 py-4 text-right text-sm font-semibold tabular-nums text-gray-700'>{cnpjData ? 1 : product.cnpj_count}</td>
                        <td className='px-5 py-4 text-right text-sm font-semibold tabular-nums text-gray-700'>{formatNumber(cnpjData?.current_stock ?? product.current_stock)}</td>
                        <td className='px-5 py-4 text-right text-sm tabular-nums text-gray-600'>{formatNumber(cnpjData?.total_sales ?? product.total_sales)}</td>
                        <td className='px-5 py-4 text-sm text-gray-600'>{formatDate(cnpjData?.last_imported_at ?? product.last_imported_at)}</td>
                        <td className='px-5 py-4'>
                          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${mixedStatus ? 'border-violet-200 bg-violet-50 text-violet-700' : status.classes}`}>
                            {mixedStatus ? 'Situação mista' : status.label}
                          </span>
                        </td>
                        <td className='px-5 py-4 text-right'>
                          <button
                            type='button'
                            onClick={() => openStatusDialog(product)}
                            className='inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50'
                          >
                            <Pencil className='h-4 w-4' />
                            Gerenciar
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className='flex flex-col gap-3 border-t border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between'>
              <p className='text-sm text-gray-500'>
                Exibindo {(currentPage - 1) * PAGE_SIZE + 1}&ndash;{Math.min(currentPage * PAGE_SIZE, filteredProducts.length)} de {filteredProducts.length} produtos
              </p>
              <div className='flex items-center gap-2'>
                <button type='button' disabled={currentPage === 1} onClick={() => setPage((value) => Math.max(1, value - 1))} className='rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 disabled:opacity-40'>
                  <ChevronLeft className='h-4 w-4' />
                </button>
                <span className='min-w-24 text-center text-sm font-semibold text-gray-700'>{currentPage} de {pageCount}</span>
                <button type='button' disabled={currentPage === pageCount} onClick={() => setPage((value) => Math.min(pageCount, value + 1))} className='rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 disabled:opacity-40'>
                  <ChevronRight className='h-4 w-4' />
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      {selectedProduct && (
        <div className='fixed inset-0 z-[12000] flex items-start justify-center overflow-y-auto bg-slate-950/55 p-3 sm:items-center sm:p-5' onMouseDown={() => !saving && setSelectedProduct(null)}>
          <div className='my-auto w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl' onMouseDown={(event) => event.stopPropagation()}>
            <div className='flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5'>
              <div className='min-w-0'>
                <h2 className='text-xl font-bold text-gray-900'>Situa&ccedil;&atilde;o do produto</h2>
                <p className='mt-1 truncate text-sm text-gray-500'>{selectedProduct.product_name} {'\u2022'} EAN {selectedProduct.ean}</p>
              </div>
              <button type='button' onClick={() => setSelectedProduct(null)} disabled={saving} className='flex-none rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700'>
                <X className='h-5 w-5' />
              </button>
            </div>
            <div className='max-h-[calc(100dvh-180px)] space-y-5 overflow-y-auto p-6'>
              <div className='rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm leading-6 text-blue-900'>
                {statusScope === '*'
                  ? 'A definição será aplicada a todos os CNPJs. Exceções anteriores deste produto serão substituídas.'
                  : `A definição será aplicada somente ao CNPJ ${formatCnpj(statusScope)}. Os demais CNPJs não serão alterados.`}
              </div>
              <label className='block text-sm font-semibold text-gray-700'>
                Aplicar esta situação em
                <select
                  value={statusScope}
                  onChange={(event) => changeStatusScope(event.target.value)}
                  className='mt-2 w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 font-normal outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                >
                  <option value='*'>Todos os CNPJs</option>
                  {selectedProduct.cnpjs.map((item) => <option key={item.cnpj} value={item.cnpj}>{formatCnpj(item.cnpj)}</option>)}
                </select>
              </label>
              <div className='grid gap-3'>
                {(Object.keys(statusConfig) as ProductLifecycleStatus[]).map((statusValue) => {
                  const option = statusConfig[statusValue]
                  return (
                    <button
                      key={statusValue}
                      type='button'
                      onClick={() => setDraftStatus(statusValue)}
                      className={`rounded-xl border p-4 text-left transition ${
                        draftStatus === statusValue
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className='flex items-center justify-between gap-3'>
                        <span className='font-semibold text-gray-900'>{option.label}</span>
                        <span className={`h-4 w-4 rounded-full border-4 ${draftStatus === statusValue ? 'border-blue-600' : 'border-gray-300'}`} />
                      </div>
                      <p className='mt-1 text-sm leading-5 text-gray-500'>{option.description}</p>
                    </button>
                  )
                })}
              </div>
              {draftStatus !== 'active' && (
                <label className='block text-sm font-semibold text-gray-700'>
                  Observa&ccedil;&atilde;o opcional
                  <textarea
                    value={statusNote}
                    onChange={(event) => setStatusNote(event.target.value)}
                    maxLength={500}
                    rows={4}
                    placeholder={'Ex.: produto substitu\u00eddo por um novo EAN...'}
                    className='mt-2 w-full resize-none rounded-xl border border-gray-300 p-3 text-sm font-normal outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                  />
                  <span className='mt-1 block text-right text-xs font-normal text-gray-400'>{statusNote.length}/500</span>
                </label>
              )}
            </div>
            <div className='flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4'>
              <button type='button' onClick={() => setSelectedProduct(null)} disabled={saving} className='rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50'>Cancelar</button>
              <button type='button' onClick={saveStatus} disabled={saving} className='inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50'>
                {saving && <Loader2 className='h-4 w-4 animate-spin' />}
                Salvar situa&ccedil;&atilde;o
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function FilterSelect({value, onChange, children}: {
  value: string
  onChange: (value: string) => void
  children: ReactNode
}) {
  return (
    <select value={value} onChange={(event) => onChange(event.target.value)} className='rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100'>
      {children}
    </select>
  )
}

function SummaryCard({icon: Icon, label, value, color, help}: {
  icon: typeof Boxes
  label: string
  value: number
  color: 'blue' | 'emerald' | 'red' | 'amber'
  help: string
}) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-600',
  }
  return (
    <div className='relative rounded-2xl border border-gray-200 bg-white p-5 shadow-sm'>
      <CardHelp content={help} className='absolute right-2 top-2' />
      <div className='flex items-center justify-between gap-4 pr-7'>
        <div><p className='text-sm text-gray-500'>{label}</p><p className='mt-1 text-3xl font-bold text-gray-900'>{value}</p></div>
        <span className={`rounded-xl p-3 ${colors[color]}`}><Icon className='h-5 w-5' /></span>
      </div>
    </div>
  )
}
